import { lazy, Suspense, type ComponentType } from 'react'
import { useParams } from 'react-router-dom'
import ContentShell from '../../components/blog/ContentShell'
import ToolSeo from '../../components/blog/ToolSeo'
import Breadcrumbs from '../../components/blog/Breadcrumbs'
import RelatedPosts from '../../components/blog/RelatedPosts'
import NotFound from '../NotFound'
import ToolErrorBoundary from '../../components/tools/ui/ToolErrorBoundary'
import { getToolBySlug } from '../../lib/blog/tools'
import { getPostBySlug } from '../../lib/blog/posts'
import type { BlogPost } from '../../lib/blog/types'

/**
 * Registry of client-side tool widgets, keyed by the `widget` frontmatter value.
 * Each is lazy-loaded so visiting one tool only downloads that widget's code (SSR renders the
 * Suspense fallback; the markdown body + JSON-LD still prerender for SEO).
 */
const WIDGETS: Record<string, ComponentType> = {
  BankStatementConverterTool: lazy(() => import('../../components/tools/BankStatementConverterTool')),
  CsvValidatorTool: lazy(() => import('../../components/tools/CsvValidatorTool')),
  DuplicateFinderTool: lazy(() => import('../../components/tools/DuplicateFinderTool')),
  TransactionCategorizerTool: lazy(() => import('../../components/tools/TransactionCategorizerTool')),
  ExpenseTrackerTool: lazy(() => import('../../components/tools/ExpenseTrackerTool')),
  CsvColumnMapperTool: lazy(() => import('../../components/tools/CsvColumnMapperTool')),
  ReconciliationMatcherTool: lazy(() => import('../../components/tools/ReconciliationMatcherTool')),
  CashFlowSummaryTool: lazy(() => import('../../components/tools/CashFlowSummaryTool')),
  CsvCleanerTool: lazy(() => import('../../components/tools/CsvCleanerTool')),
  PdfReadabilityTool: lazy(() => import('../../components/tools/PdfReadabilityTool')),
  BankStatementAnalyzerTool: lazy(() => import('../../components/tools/BankStatementAnalyzerTool')),
  MissingStatementDetectorTool: lazy(() => import('../../components/tools/MissingStatementDetectorTool')),
  RedactBankStatementTool: lazy(() => import('../../components/tools/RedactBankStatementTool')),
  CsvToQboTool: lazy(() => import('../../components/tools/CsvToQboTool')),
}

export default function ToolPage() {
  const { slug = '' } = useParams()
  const tool = getToolBySlug(slug)
  if (!tool) return <NotFound />

  const Widget = tool.widget ? WIDGETS[tool.widget] : null
  const related = tool.related_posts.map(getPostBySlug).filter((p): p is BlogPost => p !== null)

  return (
    <ContentShell>
      <ToolSeo tool={tool} />
      <div className="mx-auto max-w-3xl pt-6">
        <Breadcrumbs
          crumbs={[
            { name: 'Home', path: '/' },
            { name: 'Tools', path: '/tools' },
            { name: tool.title, path: `/tools/${tool.slug}` },
          ]}
        />
        <h1 className="text-[clamp(2rem,3vw+1rem,2.8rem)] font-bold tracking-tight text-ink leading-tight m-0">
          {tool.title}
        </h1>
        <p className="text-ink-soft mt-4 mb-8 text-lg">{tool.description}</p>

        {Widget && (
          <div className="mb-10">
            <ToolErrorBoundary>
              <Suspense
                fallback={
                  <div className="rounded-3xl border border-black/10 bg-surface p-8 text-center text-ink-soft">
                    Loading tool…
                  </div>
                }
              >
                <Widget />
              </Suspense>
            </ToolErrorBoundary>
          </div>
        )}

        <div className="prose-content" dangerouslySetInnerHTML={{ __html: tool.html }} />

        <RelatedPosts posts={related} />
      </div>
    </ContentShell>
  )
}
